import { useEffect, useState } from 'react';
import { message, Table, Button, Card, Space, Typography, Popconfirm, Col, Row, Divider, Collapse, Input, DatePicker, Select, Dropdown } from 'antd';
import { dateFormat, notifyMessages, tablePageSize } from '../../utils/constants';
import { PageHeader } from '../../components/common/pageHeader';
import { DeleteFilled, DownloadOutlined, PlusOutlined, WarningOutlined, DownOutlined } from '@ant-design/icons';
import "./attachments.scss";
import { useCurrentUserContext } from '../../context/currentUserContext';
import { isArchivio, isSuperAdmin } from '../../configs/auth/roleRights';
import NewAttachment from './newAttachment';
import AttachmentServices from '../../services/attachmentServices';
import { downloadSelectedDocPdfFiles, handleDateConvertDayJs, handleDateConvertForApis } from '../../utils/functions';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { fetchSuppliersList } from '../../state/slices/suppliersListSlice';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const initialFilters = {
    supplierID: null,
    attachmentName: "",
    dateAddedFrom: null,
    dateAddedTo: null,
    dateAddedRange: null,
};

const AttachmentList = () => {
    const { currentUser } = useCurrentUserContext();
    const dispatch = useDispatch();

    const [filters, setFilters] = useState(initialFilters);
    const [attachments, setAttachments] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
    const suppliersList = useSelector(state => state.suppliers.suppliers);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [hasAllSelection, setHasAllSelection] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCountRecords, setTotalCountRecords] = useState(null);
    const [sorterColumn, setSorterColumn] = useState("");
    const [loadingExcelDataExport, setLoadingExcelDataExport] = useState(false);

    useEffect(() => {
        if (!currentUser) return;
        dispatch(fetchSuppliersList(currentUser.lastSelectedCategory, false, currentUser.id));
    }, [dispatch, currentUser]);

    useEffect(() => {
        getAttahcments(filters);
    }, [pageNumber, sorterColumn]);

    const getAttahcments = async (appliedFilters) => {
        let filterObj = filters;
        filterObj["pageNumber"] = pageNumber;
        filterObj["sortColumn"] = sorterColumn;
        filterObj["userID"] = currentUser.id;
        filterObj["categoryID"] = currentUser.lastSelectedCategory;

        if (isArchivio(currentUser)) {
            filterObj.supplierID = currentUser.supplierID;
        }

        setLoadingData(true);
        try {
            const respData = await AttachmentServices.getAttachments(appliedFilters);
            if (respData.success) {
                const res = respData.responseObject.value.supplierAttachmentsLists;
                setAttachments(res);

                if (res && res.length > 0){
                    setTotalCountRecords(res[0].totalCount);
                } else {
                    setTotalCountRecords(0);
                }

                if (hasAllSelection) {
                    setSelectedRowKeys(res.map(doc => doc.id));
                }
            }
            else {
                message.error(notifyMessages.errorAccured);
            }
        } catch (error) {
            console.error('Failed:', error.message);
        } finally {
            setLoadingData(false);
        }
    };

    const showDrawer = (drawerName, record) => {
        if (drawerName === "create") setOpenCreateDrawer(true);
    }

    const onCloseDrawer = (drawerName) => {
        if (drawerName === "create") setOpenCreateDrawer(false);
    };


    const onRemoveAttahchment = async (fileId) => {
        try {
            const respData = await AttachmentServices.deleteAttachment(fileId);

            if (respData.success) {
                message.success(notifyMessages.operationSuccess);
                getAttahcments(filters);
            }
            else {
                message.error(notifyMessages.errorAccured);
            }

        } catch (error) {
            message.error(notifyMessages.errorAccured);
        }
    };

    const columns = [
        {
            title: "",
            key: "actions",
            fixed: "left",
            width: "40px",
            render: (_, record) => (
                <Space size="middle">
                    <Button style={{ color: "#023B72" }} size="small" shape="circle" title='Scarica' href={record.filePath} icon={<DownloadOutlined />} />
                </Space>
            ),
        },
        {
            title: 'Manutentore',
            dataIndex: 'supplierName',
            key: 'supplierName',
            showSorterTooltip: false,
            sorter: (a, b) => {
                a = a.supplierName || "";
                b = b.supplierName || "";
                return a.localeCompare(b);
            },
        },
        {
            title: 'Nome file',
            dataIndex: 'attachmentName',
            key: 'attachmentName',
            showSorterTooltip: false,
            sorter: (a, b) => {
                a = a.attachmentName || "";
                b = b.attachmentName || "";
                return a.localeCompare(b);
            },
        },
        {
            title: 'Data caricamento',
            dataIndex: 'creationDate',
            key: 'creationDate',
            showSorterTooltip: false,
            sorter: (a, b) => {
                a = a.creationDate || "";
                b = b.creationDate || "";
                return a.localeCompare(b);
            },
            render: (text) => text ? handleDateConvertDayJs(text) : ''
        },
        {
            key: "action2",
            width: "35px",
            render: (_, record) => {
                return (
                    <>
                        {(isSuperAdmin(currentUser)) && (
                            <Popconfirm
                                placement="topRight"
                                key="remove"
                                title="Vuoi eliminare questo file?"
                                icon={<WarningOutlined style={{ color: '#f7c924' }} />}
                                okText="Si"
                                cancelText="No"
                                onConfirm={() => onRemoveAttahchment(record.id)}
                            >
                                <DeleteFilled style={{ color: '#023B72' }} title={"Elimina"} />
                            </Popconfirm>
                        )}
                    </>
                )
            }
        }

    ];

    const onFilter = () => {
        getAttahcments(filters);
    }

    const onClearFilters = () => {
        setFilters(initialFilters);
        getAttahcments(initialFilters);
    }

    const handelFilterChange = (name, value) => {
        if (name === "dateAddedRange") {
            let startDateAdded = handleDateConvertForApis(value[0].$d);
            let endDateAdded = handleDateConvertForApis(value[1].$d);
            setFilters((prev) => ({ ...prev, dateAddedFrom: startDateAdded }));
            setFilters((prev) => ({ ...prev, dateAddedTo: endDateAdded }));
            setFilters((prev) => ({ ...prev, dateAddedRange: value }));
        }
        else {
            setFilters((prev) => ({ ...prev, [name]: value }));
        }
    }

    const getDocumentAttachmentsExportFiles = async () => {
        const docIds = hasAllSelection ? null : selectedRowKeys.join(",");

        setLoadingExcelDataExport(true)
        let filterObj = filters;
        filterObj["userID"] = currentUser.id;
        filterObj["idList"] = docIds;
        filterObj["categoryID"] = currentUser.lastSelectedCategory;

        try {
            const respData = await AttachmentServices.getAttachmentsFiles(filterObj);
            if (respData.success) {
                let result = respData.responseObject.value.supplierAttachmentsExports;
                if (result && result.length > 0) {
                    downloadSelectedDocPdfFiles(result, `Attachments-${dayjs().format(dateFormat)}`);
                }
                else {
                    message.info(notifyMessages.noFileFound);
                }
                handleDeselectAll();
            }
            setLoadingExcelDataExport(false);
        } catch (error) {
            message.error(notifyMessages.errorAccured);
            setLoadingExcelDataExport(false);
        }
    };

    const panelItems = [
        {
            key: '1',
            label: 'Filtra',
            children: (
                <>
                    <Row gutter={{ lg: 24 }}>

                        {!isArchivio(currentUser) && (
                            <Col className="gutter-row mb10" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 4 }}>
                                <Space direction='vertical' style={{ width: "100%" }}>
                                    <span>Manutentore</span>
                                    <Select
                                        value={filters.supplierID}
                                        style={{ width: "100%" }}
                                        showSearch
                                        optionFilterProp="label"
                                        placeholder="Manutentore"
                                        options={suppliersList}
                                        onSelect={(value) => handelFilterChange("supplierID", value)}
                                    />
                                </Space>
                            </Col>
                        )}

                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                            <Space direction='vertical'>
                                <span>Nome file</span>
                                <Input value={filters.attachmentName} onChange={(e) => handelFilterChange("attachmentName", e.target.value)} />
                            </Space>
                        </Col>

                        <Col className="gutter-row mb10" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }}>
                            <Space direction='vertical' style={{ width: "100%" }}>
                                <span>Data caricamento</span>
                                <RangePicker
                                    value={filters.dateAddedRange}
                                    style={{ width: "100%" }}
                                    format={dateFormat}
                                    placeholder={["Data inizio", 'Data fine']}
                                    onChange={(dates) => handelFilterChange("dateAddedRange", dates)}
                                />
                            </Space>
                        </Col>
                    </Row>
                    <Divider />
                    <Row gutter={{ lg: 24 }}>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Button type="primary" style={{ marginRight: 10 }} onClick={() => onFilter()}> Filtra </Button>
                            <Button onClick={() => onClearFilters()}> Rimuovi  </Button>
                        </Col>
                    </Row>
                </>
            )
        }
    ]

    const handleSelectPage = () => {
        setHasAllSelection(false);
        const selectedPageDocIds = attachments.map(doc => doc.id);
        setSelectedRowKeys(selectedPageDocIds);
    };

    const handleSelectAll = () => {
        setHasAllSelection(true);
        const allDocumentIds = attachments.map(doc => doc.id);
        setSelectedRowKeys(allDocumentIds);
    };

    const handleDeselectAll = () => {
        setSelectedRowKeys([]);
        setHasAllSelection(false);
    };

    const onPageOrOrderChange = (pagination, filters, sorter) => {
        setPageNumber(pagination.current);
        if (sorter && sorter.field) {
            let sorterQuote = "";
            if (sorter.order === "descend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "DESC";
            }
            if (sorter.order === "ascend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "ASC";
            }
            setSorterColumn(sorterQuote);
        } else {
            setSorterColumn("");
        }
    };

    const onSelectChange = (newSelectedRowKeys, selectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onChange: onSelectChange,
        columnWidth: "50px",
    };

    const hasSelected = selectedRowKeys.length > 0;
    const selectedRowsCount = selectedRowKeys.length;

    const items = (
        [
            {
                key: '1',
                label: (
                    <Text onClick={handleSelectPage}>Seleziona pagina</Text>
                )
            },
            {
                key: '2',
                label: <Text onClick={handleSelectAll}>Seleziona tutto</Text>
            }
        ])

    return (
        <div className='attachmentsContainer'>
            <NewAttachment open={openCreateDrawer} onClose={() => onCloseDrawer("create")} getAttachments={() => getAttahcments(filters)} />

            <PageHeader title="Attachments" />

            <Row gutter={{ lg: 24 }} className="filters-collapse" >
                <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                    <Collapse size='small' className='filterPanel' items={panelItems} defaultActiveKey={['1']} />
                </Col>
            </Row>

            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                <Card className="docListButtons">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space size="middle">
                                <Dropdown menu={{ items }}>
                                    <Button type="default">Seleziona {`(${selectedRowsCount})`}<DownOutlined /></Button>
                                </Dropdown>
                                <Button type="default" onClick={handleDeselectAll}>Deseleziona tutto</Button>
                            </Space>
                        </Col>

                        <Col>
                            <Space size="middle">
                                <Button type="primary" onClick={() => showDrawer("create")} icon={<PlusOutlined />}>Aggiungi</Button>
                            </Space>
                        </Col>

                    </Row>
                </Card>
            </Col>

            <Card>
                <Table
                    className='customTableRows'
                    size='small'
                    bordered
                    rowClassName={(_, index) =>
                        index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
                    }
                    loading={loadingData}
                    dataSource={attachments}
                    columns={columns}
                    rowKey={obj => obj.id}
                    rowSelection={rowSelection}
                    onChange={(pagination, filters, sorter) =>
                        onPageOrOrderChange(pagination, filters, sorter)
                    }
                    pagination={{
                        pageSize: tablePageSize,
                        showSizeChanger: false,
                        total: totalCountRecords || 0,
                        current: pageNumber
                    }}
                    tableLayout="fixed"
                />

                <Row gutter={{ lg: 24 }}>
                    <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <div className="doc-bottom-button-wrapper">
                            <Button type="primary" loading={loadingExcelDataExport} disabled={!hasSelected} onClick={getDocumentAttachmentsExportFiles} className='mr10'> Scarica {`(${hasAllSelection ? totalCountRecords : selectedRowsCount})`}</Button>
                        </div>
                    </Col>
                </Row>
            </Card>
        </div>
    )
}
export default AttachmentList;